import { useState, useRef, useCallback, useEffect } from 'react';
import { Card, Button, Tooltip, Space } from 'antd';
import { SettingOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { t } from '../../i18n/i18n';
import './WorkflowNode.scss';

const WorkflowNode = ({
  node,
  selected,
  disabled,
  onSelect,
  onPositionChange,
  onDoubleClick,
  onConnectionStart,
  onConnectionEnd,
  onDelete,
  onDuplicate,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const nodeRef = useRef(null);

  const handleMouseDown = useCallback(
    (e) => {
      if (
        disabled ||
        e.target.classList.contains('connection-point') ||
        e.target.classList.contains('connection-dot') ||
        e.target.closest('.connection-point')
      ) {
        return;
      }

      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - node.position.x,
        y: e.clientY - node.position.y,
      });
      onSelect();
    },
    [disabled, node.position, onSelect]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const newPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      };

      onPositionChange(newPosition);
    },
    [isDragging, dragStart, onPositionChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleOutputMouseDown = useCallback(
    (e, outputId) => {
      if (disabled) return;
      e.stopPropagation();
      onConnectionStart(outputId);
    },
    [disabled, onConnectionStart]
  );

  const handleInputMouseUp = useCallback(
    (e, inputId) => {
      if (disabled) return;
      e.stopPropagation();
      onConnectionEnd(inputId);
    },
    [disabled, onConnectionEnd]
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const nodeStyle = {
    left: `${node.position.x}px`,
    top: `${node.position.y}px`,
  };

  return (
    <div
      ref={nodeRef}
      className={`workflow-node ${selected ? 'nodeSelected' : ''} ${isDragging ? 'dragging' : ''} ${
        disabled ? 'nodeDisabled' : ''
      }`}
      style={{
        ...nodeStyle,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={onDoubleClick}
    >
      <Card
        className={`node-card ${isDragging ? 'dragging' : ''}`}
        size="small"
        title={
          <div className="node-header">
            <div className="node-icon" style={{ backgroundColor: node.template.color }}>
              <img
                className="node-icon"
                src={`data:image/svg+xml;base64,${btoa(node.template.icon)}`}
                alt=""
              />
            </div>
            <div className="node-title">
              <div className="node-name">{t(node.name)}</div>
              <div className="node-type">{t(node?.template?.category)}</div>
            </div>
          </div>
        }
        extra={
          <Space size="small">
            s
            <Tooltip title={t('ALKYMATION_CONFIGURE')}>
              <Button
                type="text"
                icon={<SettingOutlined />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDoubleClick();
                }}
              />
            </Tooltip>
            <Tooltip title={t('ALKYMATION_DUPLICATE')}>
              <Button
                type="text"
                icon={<CopyOutlined />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate && onDuplicate();
                }}
              />
            </Tooltip>
            <Tooltip title={t('ALKYMATION_DELETE')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete();
                }}
              />
            </Tooltip>
          </Space>
        }
      >
        <div className="node-content">
          <div className="node-description">{t(node.template.description)}</div>
          {node.template.maxInputs > 0 && (
            <div className="input-connections">
              {Array.from({ length: node.template.maxInputs }).map((_, index) => (
                <div
                  key={`input-${index}`}
                  className="connection-point input-point"
                  onMouseUp={(e) => handleInputMouseUp(e, `input-${index}`)}
                >
                  <Tooltip title={t('ALKYMATION_ENTRY_POINT')}>
                    <div className="connection-dot" />
                  </Tooltip>
                </div>
              ))}
            </div>
          )}

          {node.template.outputs.length > 0 && (
            <div className="output-connections">
              {node.template.outputs.map((output, index) => {
                const outputId = output.id || output.name || `output-${index}`;
                return (
                  <div
                    key={outputId}
                    className="connection-point output-point"
                    onMouseDown={(e) => handleOutputMouseDown(e, outputId)}
                  >
                    <Tooltip title={`${output.name}: ${output.description}`}>
                      <div className="connection-dot" />
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default WorkflowNode;
